// Dawn (Oct 2024) — Cart drawer custom fields
// Persists radio + date inputs as cart.attributes and textarea as cart.note
// via debounced POST /cart/update.js. Re-binds itself when Dawn re-renders the
// cart drawer (e.g. after add-to-cart) using a lightweight MutationObserver.

if (!customElements.get('cart-custom-fields')) {
  customElements.define('cart-custom-fields', class CartCustomFields extends HTMLElement {
    connectedCallback() {
      this.debounceMs = Number(this.dataset.debounce || 500);
      this.timers = new Map();
      this.bindInputs();
      this.formatDateInputs();
    }

    bindInputs() {
      this.querySelectorAll('input[name^="attributes["], textarea[name="note"]').forEach((el) => {
        const eventName = el.tagName === 'TEXTAREA' || el.type === 'text' ? 'input' : 'change';
        el.addEventListener(eventName, () => this.queueUpdate(el));
      });
    }

    formatDateInputs() {
      // Auto-format MM/DD/YY as the user types
      this.querySelectorAll('input[pattern="\\d{2}/\\d{2}/\\d{2}"]').forEach((input) => {
        input.addEventListener('input', (e) => {
          const digits = e.target.value.replace(/\D/g, '').slice(0, 6);
          let formatted = digits;
          if (digits.length >= 5) {
            formatted = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
          } else if (digits.length >= 3) {
            formatted = `${digits.slice(0, 2)}/${digits.slice(2)}`;
          }
          if (formatted !== e.target.value) e.target.value = formatted;
        });
      });
    }

    queueUpdate(el) {
      const key = el.name;
      clearTimeout(this.timers.get(key));
      const timer = setTimeout(() => this.sendUpdate(el), this.debounceMs);
      this.timers.set(key, timer);
    }

    async sendUpdate(el) {
      const body = {};
      if (el.name === 'note') {
        body.note = el.value;
      } else {
        const match = el.name.match(/^attributes\[(.+)\]$/);
        if (!match) return;
        body.attributes = { [match[1]]: el.value };
      }

      try {
        const res = await fetch(`${window.Shopify.routes.root}cart/update.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(body),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        this.flashSaved(el);
      } catch (err) {
        console.error('[cart-custom-fields] update failed', err);
        this.flashError(el);
      }
    }

    flashSaved(el) {
      el.classList.remove('is-error');
      el.classList.add('is-saved');
      setTimeout(() => el.classList.remove('is-saved'), 800);
    }

    flashError(el) {
      el.classList.add('is-error');
    }
  });
}

// Re-attach after Dawn re-renders the cart drawer body (cart drawer replaces its
// inner HTML on /cart/add.js, so the custom element re-runs connectedCallback
// automatically. This observer is only here to catch edge cases where the
// element is replaced without being re-instantiated.)
document.addEventListener('DOMContentLoaded', () => {
  const drawer = document.querySelector('cart-drawer');
  if (!drawer) return;

  new MutationObserver(() => {
    const fields = drawer.querySelector('cart-custom-fields');
    if (fields && !fields.timers) {
      fields.connectedCallback?.();
    }
  }).observe(drawer, { childList: true, subtree: true });
});
